import {
    findMatchingContentPermissionPattern,
    getContentPermission,
    type HiveTokenMetadata,
    isContentPermissionGlob,
    parseAuthToken,
} from '@gitbook/hive-core';
import { createHTTPError, HTTPError, HTTPStatusCode } from '@gitbook/util-debug';
import {
    createCache,
    type SuspenceBrowserCache,
    type SuspenseCache,
    SuspenseCacheEntryState,
} from '@gitbook/util-suspense-cache';

/**
 * Callback to fetch an authentication token for a given content.
 * The function can return multiple tokens that will be indexed for all content they reference.
 */
export type FetchHiveContentAuthTokenCallback = (contentId: string) => Promise<string | string[]>;

export type HiveAuthTokenCacheData = { token: string; metadata: HiveTokenMetadata };

/**
 * Manage JWT tokens for the HiveClient.
 * Tokens are persisted in the browser cache.
 */
export class HiveAuthTokenManager {
    private browserCache: SuspenceBrowserCache<HiveAuthTokenCacheData> | undefined;
    private cache: SuspenseCache<string, HiveAuthTokenCacheData>;

    constructor(
        fetchContentAuthToken: FetchHiveContentAuthTokenCallback,
        options: {
            /**
             * When used in the browser, pass a `SuspenseBrowserCache` to automatically
             * persist tokens in the browser's cache IndexedDB.
             * This is passed as an argument when creating a new instance because creating a new one
             * with the instance can lead to multiple executions of the initialization effects due
             * to React concurrent mode, and we also don't need one when running server-side.
             * The consumer is thus responsible for creating an instance of `SuspenceBrowserCache`
             * that is stable across the application and against concurrent renderings.
             */
            browserCache?: SuspenceBrowserCache<HiveAuthTokenCacheData>;
        } = {}
    ) {
        this.browserCache = options.browserCache;

        /**
         * Maintain an internal cache of contentIds to tokens. Note that contentIds
         * can either be absolute ids or glob patterns. This cache stores both, but
         * only supports reading from the source for absolute ids. You should
         * not read glob patterns from this cache.
         */
        this.cache = createCache({
            getKey: (contentId) => contentId,
            getValue: async (contentId) => {
                // Globs are only added to the cache programmatically when a regular contentId
                // is accessed.
                if (isContentPermissionGlob(contentId)) {
                    throw new Error('content permissions patterns must be cached before access');
                }

                // Fetch the token for this contentId, it may come with other contentIds.
                const fetched = await fetchContentAuthToken(contentId);
                const tokens = Array.isArray(fetched) ? fetched : [fetched];

                let result: HiveAuthTokenCacheData | undefined;

                tokens.forEach((token) => {
                    const metadata = parseAuthToken(token);
                    const entry = { token, metadata };

                    // Tokens can contain permissions for multiple contents, we cache them all.
                    Object.keys(metadata.payload.content).forEach((tokenContentId) => {
                        const permission = getContentPermission(contentId, metadata.payload);
                        if (permission) {
                            result = entry;
                        }

                        if (contentId !== tokenContentId) {
                            this.cache.write(tokenContentId, entry);
                        }
                    });
                });

                if (!result) {
                    throw new createHTTPError.Forbidden(
                        `No authentication for content "${contentId}"`
                    );
                }

                return result;
            },
            ttl: (value) => value.metadata.expiration - (Date.now() + 5 * 60 * 1000),
            effects: this.browserCache ? [this.browserCache.effect] : [],
        });
    }

    /**
     * Get the parsed version of an authentication token.
     */
    public async getParsedToken(contentId: string): Promise<HiveAuthTokenCacheData> {
        if (this.cache.getCacheState(contentId)) {
            return this.read(contentId);
        }

        // The contentId is not in the cache but a matching pattern might be, look for one.
        const validKeys = Array.from(this.cache.keys()).filter((key) => {
            const state = this.cache.getCacheState(key);
            return state && state.state !== SuspenseCacheEntryState.FAILED;
        });

        const matchingKey = findMatchingContentPermissionPattern(contentId, validKeys);

        if (matchingKey) {
            // If we find a matching pattern, re-cache it under the contentId (for faster lookups in the future) and return it.
            const result = await this.read(matchingKey);
            this.cache.write(contentId, result);
            return result;
        }

        return this.read(contentId);
    }

    /**
     * Get an authentication token to use for a specific content.
     */
    public async getToken(contentId: string): Promise<string> {
        if (isContentPermissionGlob(contentId)) {
            throw new Error(
                'HiveAuthTokenManager.getToken called with a glob pattern, only absolute ids are allowed.'
            );
        }

        const { token } = await this.getParsedToken(contentId);
        return token;
    }

    /**
     * Clear the cache stored in the browser.
     * This method should be called when user is logged out.
     */
    public async clearCache() {
        this.cache.clearAll();
        if (this.browserCache) {
            await this.browserCache.clearCache();
        }
    }

    /**
     * Read a token from the cache, forgetting the entry when the failure is transient.
     *
     * A failed cache entry is never expired by its TTL, so without this a single network blip
     * or server error would keep rejecting for the lifetime of the page and permanently strand
     * everything built on top of the token — realtime sync included.
     */
    private async read(key: string): Promise<HiveAuthTokenCacheData> {
        try {
            return await this.cache.readAsync(key);
        } catch (error) {
            if (isTransientTokenError(error)) {
                // Only forget the entry that actually failed. Concurrent readers settle on the
                // same rejection, and by the time the later ones run a fresh fetch may already
                // be in flight — clearing that would start a duplicate token request.
                const state = this.cache.getCacheState(key);
                if (state?.state === SuspenseCacheEntryState.FAILED) {
                    this.cache.clear(key);
                }
            }
            throw error;
        }
    }
}

/**
 * Whether a token failure is worth retrying.
 *
 * Only failures that mean "the request never got an answer" qualify: the browser is offline, the
 * request was aborted, or the server failed to produce one. Everything else is a deterministic
 * answer — a permission decision, a token that does not parse — and stays cached, because
 * retrying it turns every reconnection attempt into another identical request that fails the
 * same way.
 */
function isTransientTokenError(error: unknown): boolean {
    if (error instanceof HTTPError) {
        return (
            error.statusCode >= HTTPStatusCode.InternalServerError ||
            error.statusCode === HTTPStatusCode.RequestTimeout ||
            error.statusCode === HTTPStatusCode.TooManyRequests
        );
    }

    // `fetch` rejects with a TypeError when the request never reached the server, and with an
    // AbortError when it was cancelled.
    return error instanceof TypeError || (error instanceof Error && error.name === 'AbortError');
}
